import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { AutoFocusModule } from 'primeng/autofocus';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ProfileDataService } from 'src/app/services/profile-data.service';
import { IonContent, IonHeader, NavController } from "@ionic/angular/standalone";

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.page.html',
  styleUrls: ['./onboarding.page.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonHeader,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    AutoFocusModule,
    CommonModule
  ],
})
export class OnboardingPage implements OnInit {
  personalDetailsForm: FormGroup;
  loading = false;
  environment = environment;
  maxDate = new Date();

  isEmailVerified = false;
  emailSent = false;
  changingEmail = false;

  regionalHubs: any[] = [];

  constructor(
    private _fb: FormBuilder,
    private _toast: ToastService,
    private _http: HttpClient,
    private _nav: NavController,
    private _profile: ProfileDataService,
    private _auth: AuthService
  ) {
    this.personalDetailsForm = this._fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      date_of_birth: ['', [Validators.required, Validators.pattern(/^\d{2}\.\d{2}\.\d{4}$/)]],
      regional_hub_id: [null, [Validators.required]]
    });
  }

  async ngOnInit() {
    // Check if email is verified
    this._auth.isEmailVerified$.subscribe(verified => {
      this.isEmailVerified = verified ?? false;
    });

    // Load regional hubs
    this.loadRegionalHubs();
  }

  async loadRegionalHubs() {
    const response = await firstValueFrom(
      this._http.get<any>(`${environment.apiUrl}/experience/users/hubs`)
    );


    this.regionalHubs = response.map((hub: any) => ({
      label: `${hub.name}`,
      value: hub.id
    }));


  }

  async sendVerificationEmail() {
    this.emailSent = true;
    try {
      await this._auth.sendEmailVerification();
      this._toast.add({
        severity: 'success',
        summary: 'E-Mail gesendet',
        detail: 'Bestätigungs-E-Mail wurde erneut gesendet.'
      });
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'E-Mail konnte nicht gesendet werden.'
      });
    } finally {
      this.emailSent = false;
    }
  }

  async checkEmailVerification() {
    this.loading = true;
    try {
      const verified = await this._auth.reloadAndCheckEmailVerified();
      if (verified) {
        this.isEmailVerified = true;
        this._toast.add({
          severity: 'success',
          summary: 'Bestätigt',
          detail: 'Deine E-Mail wurde erfolgreich bestätigt!'
        });
      } else {
        this._toast.add({
          severity: 'info',
          summary: 'Noch nicht bestätigt',
          detail: 'Bitte klicke auf den Link in der E-Mail.'
        });
      }
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Status konnte nicht geprüft werden.'
      });
    } finally {
      this.loading = false;
    }
  }

  async useOtherEmail() {
    this.changingEmail = true;
    try {
      await firstValueFrom(
        this._http.delete(`${environment.apiUrl}/experience/users/bye`)
      );
      await firstValueFrom(this._auth.logout());
      this._nav.navigateRoot(['/auth/register']);
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Konto konnte nicht gelöscht werden.'
      });
    } finally {
      this.changingEmail = false;
    }
  }
  formatDateInput(event: any) {
    const isDeleting = event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward';

    if (isDeleting) {
      let value = event.target.value;
      // Hängenden Punkt beim Löschen automatisch mitentfernen
      if (value.endsWith('.')) {
        value = value.slice(0, -1);
        event.target.value = value;
      }
      this.personalDetailsForm.patchValue({ date_of_birth: event.target.value }, { emitEvent: false });
      return;
    }

    let input = event.target.value.replace(/\D/g, '');
    if (input.length >= 2) {
      input = input.substring(0, 2) + '.' + input.substring(2);
    }
    if (input.length >= 5) {
      input = input.substring(0, 5) + '.' + input.substring(5, 9);
    }
    event.target.value = input;
    this.personalDetailsForm.patchValue({ date_of_birth: input }, { emitEvent: false });
  } 

  async completeOnboarding() {
    if (this.personalDetailsForm.invalid) {
      Object.keys(this.personalDetailsForm.controls).forEach((key) => {
        const control: any = this.personalDetailsForm.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });

      this._toast.add({
        severity: 'contrast',
        summary: 'Fehlende Daten',
        detail: 'Bitte fülle alle Felder aus.'
      });
      return;
    }

    this.loading = true;
    this.personalDetailsForm.disable();

    try {
      // Convert dd.mm.yyyy to yyyy-mm-dd
      const dateStr = this.personalDetailsForm.value.date_of_birth;
      let isoDate = null;
      if (dateStr && dateStr.length === 10) {
        const [day, month, year] = dateStr.split('.');
        isoDate = `${year}-${month}-${day}`;
      }

      const formData = {
        ...this.personalDetailsForm.value,
        date_of_birth: isoDate
      };

      await firstValueFrom(
        this._http.post(`${environment.apiUrl}/experience/users`, formData)
      );

      await this._profile.loadUserDetails();
      await this._auth.refreshClaims();

      this._toast.add({
        severity: 'success',
        summary: 'Profil erstellt',
        detail: 'Dein Profil wurde erfolgreich erstellt!'
      });

      setTimeout(() => {
        this._nav.navigateRoot(['/dashboard']);
      }, 500);
    } catch (error: any) {
      this.loading = false;
      this.personalDetailsForm.enable();

      console.error('Error completing onboarding:', error);

      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler',
        detail: 'Profil konnte nicht erstellt werden.'
      });
    }
  }
}
